import { ReorderableArray } from "@corpuscomplete/core-ui/form/reorderable-array";
import {
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { restrictToFirstScrollableAncestor } from "@dnd-kit/modifiers";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { flushSync } from "react-dom";

type Rect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

type Offset = {
  x: number;
  y: number;
};

type Session = {
  activeId: string;
  signature: string;
  orderIds: readonly string[];
  rects: Record<string, Rect>;
  gap: number;
};

type ReorderResult<TItem> = {
  activeId: string;
  overId: string;
  projectedItems: readonly TItem[];
  dropTarget: ReorderableArray.DropTarget;
};

type Options<TItem> = {
  items: readonly TItem[];
  getItemId: (item: TItem) => string;
  getItemSignature?: (item: TItem) => string;
  onReorder?: (result: ReorderResult<TItem>) => void | Promise<void>;
  activationDistance?: number;
};

type RowStyle = {
  wrapper: CSSProperties;
  item: CSSProperties;
};

type FlipState = {
  phase: "from" | "to";
  transforms: Record<string, { x: number; y: number }>;
};

type HookResult<TItem> = {
  activeId: string | null;
  activeItem: TItem | null;
  dndContextProps: {
    sensors: ReturnType<typeof useSensors>;
    modifiers: Array<typeof restrictToFirstScrollableAncestor>;
    onDragStart: (event: DragStartEvent) => void;
    onDragMove: (event: DragMoveEvent) => void;
    onDragCancel: () => void;
    onDragEnd: (event: DragEndEvent) => ReorderResult<TItem> | null;
  };
  getRowProps: (id: string) => {
    id: string;
    wrapperStyle: CSSProperties;
    itemStyle: CSSProperties;
    registerRowElement: (element: HTMLDivElement | null) => void;
  };
  getRowStyle: (id: string, isDragging: boolean) => RowStyle;
  overlayWidth: number | undefined;
};

export function useVerticalDragReorder<TItem>({
  items,
  getItemId,
  getItemSignature,
  onReorder,
  activationDistance = 8,
}: Options<TItem>): HookResult<TItem> {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: activationDistance },
    }),
  );
  const [dragSession, setDragSession] = useState<Session | null>(null);
  const [dragOffset, setDragOffset] = useState<Offset>({ x: 0, y: 0 });
  const [flipState, setFlipState] = useState<FlipState | null>(null);
  const rowElementsRef = useRef<Record<string, HTMLDivElement | null>>({});
  const pendingFlipRectsRef = useRef<Record<string, Rect> | null>(null);
  const pendingReorderOriginIdsRef = useRef<string | null>(null);
  const flipFrameRef = useRef<number | null>(null);
  const flipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const itemIds = useMemo(() => items.map((item) => getItemId(item)), [getItemId, items]);
  const itemsSignature = useMemo(
    () =>
      items
        .map((item) => {
          const id = getItemId(item);
          return getItemSignature ? `${id}:${getItemSignature(item)}` : id;
        })
        .join("|"),
    [getItemId, getItemSignature, items],
  );
  const projectedOverId = getProjectedOverId({ dragSession, dragOffset });
  const projectedItems = useMemo(
    () =>
      getProjectedItems({
        items,
        dragSession,
        overId: projectedOverId,
        getItemId,
      }),
    [dragSession, getItemId, items, projectedOverId],
  );
  const activeItem = useMemo(
    () =>
      dragSession ? (items.find((item) => getItemId(item) === dragSession.activeId) ?? null) : null,
    [dragSession, getItemId, items],
  );
  const activeId = dragSession?.activeId ?? null;
  const itemOrderSignature = useMemo(() => itemIds.join("|"), [itemIds]);

  const clearFlipAnimation = useCallback(() => {
    if (flipFrameRef.current) {
      cancelAnimationFrame(flipFrameRef.current);
      flipFrameRef.current = null;
    }

    if (flipTimeoutRef.current) {
      clearTimeout(flipTimeoutRef.current);
      flipTimeoutRef.current = null;
    }

    setFlipState(null);
  }, []);

  const playFlipAnimation = useCallback(
    (previousRects: Record<string, Rect>, nextRects: Record<string, Rect>) => {
      const transforms = getFlipTransforms(previousRects, nextRects);
      if (Object.keys(transforms).length === 0) {
        clearFlipAnimation();
        return;
      }

      if (flipFrameRef.current) {
        cancelAnimationFrame(flipFrameRef.current);
      }

      if (flipTimeoutRef.current) {
        clearTimeout(flipTimeoutRef.current);
      }

      setFlipState({ phase: "from", transforms });
      flipFrameRef.current = requestAnimationFrame(() => {
        setFlipState({ phase: "to", transforms });
        flipFrameRef.current = null;
        flipTimeoutRef.current = setTimeout(() => {
          setFlipState(null);
          flipTimeoutRef.current = null;
        }, 180);
      });
    },
    [clearFlipAnimation],
  );

  const resetDrag = useCallback(() => {
    setDragSession(null);
    setDragOffset({ x: 0, y: 0 });
  }, []);

  const registerRowElement = useCallback((id: string, element: HTMLDivElement | null) => {
    rowElementsRef.current[id] = element;
  }, []);

  const captureRects = useCallback(() => {
    return captureRectsForItems({
      items,
      getItemId,
      elementsById: rowElementsRef.current,
    });
  }, [getItemId, items]);

  useEffect(() => {
    if (!dragSession) {
      return;
    }

    if (dragSession.signature !== itemsSignature) {
      resetDrag();
    }
  }, [dragSession, itemsSignature, resetDrag]);

  useEffect(() => {
    if (!dragSession) {
      document.body.style.removeProperty("cursor");
      return;
    }

    document.body.style.setProperty("cursor", "grabbing");
    return () => {
      document.body.style.removeProperty("cursor");
    };
  }, [dragSession]);

  useEffect(() => {
    return () => {
      if (flipFrameRef.current) {
        cancelAnimationFrame(flipFrameRef.current);
      }

      if (flipTimeoutRef.current) {
        clearTimeout(flipTimeoutRef.current);
      }
    };
  }, []);

  useLayoutEffect(() => {
    const previousRects = pendingFlipRectsRef.current;
    if (!previousRects) {
      return;
    }

    const originOrderSignature = pendingReorderOriginIdsRef.current;
    if (originOrderSignature && originOrderSignature === itemOrderSignature) {
      return;
    }

    pendingFlipRectsRef.current = null;
    pendingReorderOriginIdsRef.current = null;
    const nextRects = captureRects();
    if (!nextRects) {
      return;
    }

    playFlipAnimation(previousRects, nextRects);
  }, [captureRects, itemOrderSignature, itemsSignature, playFlipAnimation]);

  const onDragStart = useCallback(
    (event: DragStartEvent) => {
      clearFlipAnimation();
      pendingFlipRectsRef.current = null;
      pendingReorderOriginIdsRef.current = null;

      const activeId = String(event.active.id);
      const rects = captureRects();
      if (!rects || !rects[activeId]) {
        resetDrag();
        return;
      }

      setDragSession({
        activeId,
        signature: itemsSignature,
        orderIds: itemIds,
        rects,
        gap: getItemGap({ items, getItemId, rects }),
      });
      setDragOffset({ x: 0, y: 0 });
    },
    [captureRects, clearFlipAnimation, getItemId, itemIds, items, itemsSignature, resetDrag],
  );

  const onDragMove = useCallback((event: DragMoveEvent) => {
    setDragOffset({ x: event.delta.x, y: event.delta.y });
  }, []);

  const onDragCancel = useCallback(() => {
    const previousRects = dragSession ? captureRects() : null;

    // While dragging, the active row element stays in its original DOM position (hidden via opacity)
    // while the drag overlay moves. Adjust the active rect so FLIP animates from the dragged offset.
    if (previousRects && dragSession) {
      const activeRect = previousRects[dragSession.activeId];
      if (activeRect) {
        activeRect.top += dragOffset.y;
        activeRect.left += dragOffset.x;
      }
    }

    pendingFlipRectsRef.current = null;
    pendingReorderOriginIdsRef.current = null;
    if (!previousRects) {
      resetDrag();
      return;
    }

    flushSync(() => {
      resetDrag();
    });

    const nextRects = captureRects();
    if (!nextRects) {
      return;
    }

    playFlipAnimation(previousRects, nextRects);
  }, [captureRects, dragOffset, dragSession, playFlipAnimation, resetDrag]);

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!dragSession) {
        setDragOffset({ x: 0, y: 0 });
        return null;
      }

      const previousRects = captureRects();
      const activeTranslatedRect = getActiveTranslatedRect(event);
      if (previousRects && activeTranslatedRect) {
        previousRects[String(event.active.id)] = activeTranslatedRect;
      }

      const activeId = String(event.active.id);
      const dropTarget = ReorderableArray.getDropTargetFromOverId({
        items,
        getItemId,
        activeId,
        overId: projectedOverId,
      });

      if (!dropTarget || !projectedOverId) {
        pendingFlipRectsRef.current = null;
        pendingReorderOriginIdsRef.current = null;
        flushSync(() => {
          resetDrag();
        });

        const nextRects = captureRects();
        if (previousRects && nextRects) {
          playFlipAnimation(previousRects, nextRects);
        }

        return null;
      }

      const result: ReorderResult<TItem> = {
        activeId,
        overId: projectedOverId,
        projectedItems,
        dropTarget,
      };

      pendingFlipRectsRef.current = previousRects;
      pendingReorderOriginIdsRef.current = dragSession.orderIds.join("|");
      void onReorder?.(result);
      resetDrag();
      return result;
    },
    [
      dragSession,
      dragOffset,
      captureRects,
      getItemId,
      items,
      onReorder,
      playFlipAnimation,
      projectedItems,
      projectedOverId,
      resetDrag,
    ],
  );

  const getRowStyle = useCallback(
    (id: string, isDragging: boolean): RowStyle => {
      const transform = getItemDragTransform({
        id,
        dragSession,
        projectedItems,
        getItemId,
      });

      return {
        wrapper: {
          ...getVerticalDragReorderWrapperStyle(
            {
              transform: transform
                ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
                : undefined,
              transition: dragSession && !isDragging ? "transform 180ms ease" : undefined,
              willChange: dragSession && !isDragging ? "transform" : undefined,
            },
            flipState,
            id,
          ),
        },
        item: {
          opacity: isDragging ? 0 : 1,
        },
      };
    },
    [dragSession, flipState, getItemId, projectedItems],
  );

  const getOverlayWidth = useCallback(() => {
    return dragSession ? dragSession.rects[dragSession.activeId]?.width : undefined;
  }, [dragSession]);

  const dndContextProps = useMemo(
    () => ({
      sensors,
      modifiers: [restrictToFirstScrollableAncestor],
      onDragStart,
      onDragMove,
      onDragCancel,
      onDragEnd,
    }),
    [onDragCancel, onDragEnd, onDragMove, onDragStart, sensors],
  );

  const getRowProps = useCallback(
    (id: string) => {
      const rowStyle = getRowStyle(id, activeId === id);

      return {
        id,
        wrapperStyle: rowStyle.wrapper,
        itemStyle: rowStyle.item,
        registerRowElement: (element: HTMLDivElement | null) => {
          registerRowElement(id, element);
        },
      };
    },
    [activeId, getRowStyle, registerRowElement],
  );

  return {
    activeId,
    activeItem,
    dndContextProps,
    getRowProps,
    getRowStyle,
    overlayWidth: getOverlayWidth(),
  };
}

function getVerticalDragReorderWrapperStyle(
  dragWrapperStyle: CSSProperties,
  flipState: FlipState | null,
  itemId: string,
): CSSProperties {
  const flipTransform = flipState?.transforms[itemId];

  return {
    transform:
      dragWrapperStyle.transform ??
      (flipState?.phase === "from" && flipTransform
        ? `translate3d(${flipTransform.x}px, ${flipTransform.y}px, 0)`
        : undefined),
    transition:
      dragWrapperStyle.transition ??
      (flipState?.phase === "to" && flipTransform ? "transform 180ms ease" : undefined),
    willChange: dragWrapperStyle.willChange ?? (flipTransform ? "transform" : undefined),
  };
}

function getItemDragTransform<TItem>(args: {
  id: string;
  dragSession: Session | null;
  projectedItems: readonly TItem[];
  getItemId: (item: TItem) => string;
}): { x: number; y: number } | null {
  return getItemTransform(args);
}

function moveItem<T>(items: readonly T[], fromIndex: number, toIndex: number): T[] {
  const nextItems = [...items];
  const [item] = nextItems.splice(fromIndex, 1);
  if (item === undefined) {
    return nextItems;
  }

  nextItems.splice(toIndex, 0, item);
  return nextItems;
}

function captureRectsForItems<TItem>(args: {
  items: readonly TItem[];
  getItemId: (item: TItem) => string;
  elementsById: Record<string, HTMLDivElement | null>;
}): Record<string, Rect> | null {
  const { items, getItemId, elementsById } = args;
  const rects: Record<string, Rect> = {};

  for (const item of items) {
    const id = getItemId(item);
    const element = elementsById[id];
    if (!element) {
      return null;
    }

    const rect = element.getBoundingClientRect();
    rects[id] = {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    };
  }

  return rects;
}

function getItemGap<TItem>(args: {
  items: readonly TItem[];
  getItemId: (item: TItem) => string;
  rects: Record<string, Rect>;
}): number {
  const { items, getItemId, rects } = args;

  for (let index = 0; index < items.length - 1; index += 1) {
    const current = rects[getItemId(items[index] as TItem)];
    const next = rects[getItemId(items[index + 1] as TItem)];
    if (!current || !next) {
      continue;
    }

    return next.top - (current.top + current.height);
  }

  return 0;
}

function getProjectedTops(
  dragSession: Session,
  projectedIds: readonly string[],
): Record<string, number> {
  const firstRect = dragSession.rects[dragSession.orderIds[0] ?? ""];
  if (!firstRect) {
    return {};
  }

  const projectedTops: Record<string, number> = {};
  let top = firstRect.top;

  for (const id of projectedIds) {
    const rect = dragSession.rects[id];
    if (!rect) {
      continue;
    }

    projectedTops[id] = top;
    top += rect.height + dragSession.gap;
  }

  return projectedTops;
}

function getProjectedItems<TItem>(args: {
  items: readonly TItem[];
  dragSession: Session | null;
  overId: string | null;
  getItemId: (item: TItem) => string;
}): readonly TItem[] {
  const { items, dragSession, overId, getItemId } = args;

  if (!dragSession || !overId || overId === dragSession.activeId) {
    return items;
  }

  const activeIndex = items.findIndex((item) => getItemId(item) === dragSession.activeId);
  const overIndex = items.findIndex((item) => getItemId(item) === overId);
  if (activeIndex === -1 || overIndex === -1) {
    return items;
  }

  return moveItem(items, activeIndex, overIndex);
}

function getProjectedOverId(args: {
  dragSession: Session | null;
  dragOffset: Offset;
}): string | null {
  const { dragSession, dragOffset } = args;
  if (!dragSession) {
    return null;
  }

  const activeRect = dragSession.rects[dragSession.activeId];
  if (!activeRect) {
    return null;
  }

  const activeCenterX = activeRect.left + dragOffset.x + activeRect.width / 2;
  const activeCenterY = activeRect.top + dragOffset.y + activeRect.height / 2;
  let closestId = dragSession.activeId;
  let closestDistance = Number.POSITIVE_INFINITY;

  for (const id of dragSession.orderIds) {
    const rect = dragSession.rects[id];
    if (!rect) {
      continue;
    }

    const dx = activeCenterX - (rect.left + rect.width / 2);
    const dy = activeCenterY - (rect.top + rect.height / 2);
    const distance = Math.hypot(dx, dy);
    if (distance < closestDistance) {
      closestId = id;
      closestDistance = distance;
    }
  }

  return closestId;
}

function getItemTransform<TItem>(args: {
  id: string;
  dragSession: Session | null;
  projectedItems: readonly TItem[];
  getItemId: (item: TItem) => string;
}): { x: number; y: number } | null {
  const { id, dragSession, projectedItems, getItemId } = args;
  if (!dragSession || id === dragSession.activeId) {
    return null;
  }

  const fromRect = dragSession.rects[id];
  if (!fromRect) {
    return null;
  }

  const projectedTops = getProjectedTops(
    dragSession,
    projectedItems.map((item) => getItemId(item)),
  );
  const projectedTop = projectedTops[id];
  if (projectedTop === undefined || projectedTop === fromRect.top) {
    return null;
  }

  return {
    x: 0,
    y: projectedTop - fromRect.top,
  };
}

function getActiveTranslatedRect(event: DragEndEvent): Rect | null {
  const rect = event.active.rect.current.translated;
  if (!rect) {
    return null;
  }

  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
}

function getFlipTransforms(
  previousRects: Record<string, Rect>,
  nextRects: Record<string, Rect>,
): Record<string, { x: number; y: number }> {
  const transforms: Record<string, { x: number; y: number }> = {};

  for (const [id, nextRect] of Object.entries(nextRects)) {
    const previousRect = previousRects[id];
    if (!previousRect) {
      continue;
    }

    const x = previousRect.left - nextRect.left;
    const y = previousRect.top - nextRect.top;
    if (x !== 0 || y !== 0) {
      transforms[id] = { x, y };
    }
  }

  return transforms;
}
